
import * as React from 'react';
import { IUpdateActionOptions, ActionHost } from '../action/Action';

/**
 * @public
 */
export interface IAutoActionProps {
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

/**
 * @internal
 */
export interface IAutoActionState {
  isAvailable?: boolean;
  execute?: () => Promise<void>;
  execution?: Promise<void>;
  isComplete?: boolean;
  error?: Error;
}

/**
 * @public
 */
export function asAutoAction(): (<P extends {}>(
  component: React.ComponentType<P>
) => React.ComponentType<P & IAutoActionProps>) {
  return <P extends {}>(
    component: React.ComponentType<P>
  ): React.ComponentType<P & IAutoActionProps> => {
    const Component = component;

    class AutoAction extends React.PureComponent<P & IAutoActionProps, IAutoActionState> {
      constructor(props: P & IAutoActionProps) {
        super(props);

        this.state = {};
      }

      public render(): JSX.Element {
        const {
          onComplete,
          onError,
          ...props
        } = this.props as IAutoActionProps;

        return (
          <ActionHost
            actionKey=''
            update={ this._update }
            remove={ this._remove }
          >
            <Component { ...props } />
          </ActionHost>
        );
      }

      public componentDidMount(): void {
        if (this._mayExecute(this.state, this.props)) {
          this._execute();
        }
      }

      public componentDidUpdate(previousProps: {}, previousState: IAutoActionState): void {
        const {
          onComplete,
          onError
        } = this.props;

        const {
          error
        } = this.state;

        if (this.state.isComplete) {
          this.setState({
            isComplete: false
          });

          if (onComplete) {
            onComplete();
          }
        }

        if (error) {
          this.setState({
            error: undefined
          });

          if (onError) {
            onError(error);
          }
        }

        if (!this._mayExecute(previousState, previousProps) && this._mayExecute(this.state, this.props)) {
          this._execute();
        }
      }

      private _mayExecute(state: IAutoActionState, props: IAutoActionProps): boolean {
        return !!state.isAvailable && !!state.execute && !state.execution && !state.isComplete && !state.error;
      }

      private async _execute(): Promise<void> {
        const {
          execute
        } = this.state;

        if (execute) {
          try {
            const execution = execute();

            this.setState({
              execution: execution
            });

            await execution;

            this.setState({
              execution: undefined,
              isComplete: true
            });
          } catch (e) {
            this.setState({
              execution: undefined,
              error: e
            });
          }
        }
      }

      private _update = (options: IUpdateActionOptions): void => {
        this.setState({
          isAvailable: options.isAvailable,
          execute: options.execute
        });
      }

      private _remove = (): void => {
        this.setState({
          isAvailable: false,
          execute: undefined
        });
      }
    }

    return AutoAction;
  };
}
